import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@lib/api";
import { QUERY_KEY } from "./useGetNotifications";
import { API_ROUTES } from "../constants";

interface Notification {
  id: string;
  isRead: boolean;
  isDeleted: boolean;
}

export const useUpdateNotificationByIds = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Notification[]) => {
      // fire and forget
      await apiClient.post(
        API_ROUTES.notifications,
        payload.map((item) => ({
          notificationId: item.id,
          isRead: item.isRead,
          isDeleted: item.isDeleted,
        }))
      );
    },
    onMutate: async (payloadNotifications: Notification[]) => {
      // Cancel any outgoing fetches for get notifications
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
      // Snapshot the previous value
      const previousNotifications = queryClient.getQueryData<Notification[]>([
        QUERY_KEY,
      ]);

      // Optimistically update state
      // This will update the notifications in the cache with the new values
      // It will not update the server, but it will make the UI feel snappier
      // and give the user immediate feedback
      queryClient.setQueryData<Notification[]>([QUERY_KEY], (prevState) => {
        if (!prevState) {
          return [];
        }
        return prevState.map((notification: Notification) => {
          // check if notification in the payload
          const updatedNotification = payloadNotifications.find(
            (payloadNotification) => payloadNotification.id === notification.id
          );
          // If it is
          if (updatedNotification) {
            // update the notification with the new values
            const result = {
              ...notification,
              isRead: updatedNotification.isRead,
              isDeleted: updatedNotification.isDeleted,
            };
            return result;
          }
          return notification;
        });
      });

      return { previousNotifications };
    },
    onError: (
      error: unknown,
      _,
      context: { previousNotifications?: Notification[] } | undefined
    ) => {
      console.error("Failed to update notification:", error);
      // rollback to previous state
      if (context?.previousNotifications) {
        queryClient.setQueryData([QUERY_KEY], context.previousNotifications);
      }
    },
    // refetch after mutation
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
